import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Save, Search, DollarSign, Package, Plus, X, Upload, Image as ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', description: '', price: '', cost: '', stock: '100', purity: '99%+', molecular_weight: '', image_url: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (JPG, PNG, WebP, or GIF)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image is too large — maximum 5 MB');
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAddForm((prev) => ({ ...prev, image_url: data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/products`, { withCredentials: true });
      setProducts(data);
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const startEditing = (product) => {
    setEditingId(product.id);
    setEditForm({
      name: product.name,
      price: product.price,
      cost: product.cost || 0,
      stock: product.stock,
      purity: product.purity || '',
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({});
  };

  const addProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/products`, {
        name: addForm.name,
        description: addForm.description,
        price: parseFloat(addForm.price),
        category: 'Research',
        stock: parseInt(addForm.stock) || 100,
        purity: addForm.purity,
        molecular_weight: addForm.molecular_weight,
        image_url: addForm.image_url,
      }, { withCredentials: true });
      // Also set cost if provided
      if (addForm.cost) {
        const prods = await axios.get(`${API}/admin/products`, { withCredentials: true });
        const newProd = prods.data.find(p => p.name === addForm.name);
        if (newProd) {
          await axios.put(`${API}/admin/products/${newProd.id}`, { cost: parseFloat(addForm.cost) }, { withCredentials: true });
        }
      }
      toast.success('Product added');
      setShowAddForm(false);
      setAddForm({ name: '', description: '', price: '', cost: '', stock: '100', purity: '99%+', molecular_weight: '', image_url: '' });
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add product');
    } finally {
      setSaving(false);
    }
  };

  const saveProduct = async (productId) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/products/${productId}`, {
        name: editForm.name,
        price: parseFloat(editForm.price),
        cost: parseFloat(editForm.cost) || 0,
        stock: parseInt(editForm.stock),
        purity: editForm.purity,
      }, { withCredentials: true });
      toast.success('Product updated');
      setEditingId(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  // Summary stats
  const totalValue = products.reduce((s, p) => s + p.price * p.stock, 0);
  const avgMargin = products.filter(p => p.cost > 0).length > 0
    ? products.filter(p => p.cost > 0).reduce((s, p) => s + ((p.price - p.cost) / p.price * 100), 0) / products.filter(p => p.cost > 0).length
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="admin-products-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </Link>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Product Management
          </h1>
          <div className="flex items-center gap-3">
            <span className="text-[#A1A7B5]">{products.length} products</span>
            <Button onClick={() => setShowAddForm(!showAddForm)} className="btn-primary flex items-center gap-2" data-testid="add-product-btn">
              {showAddForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showAddForm ? 'Cancel' : 'Add Product'}
            </Button>
          </div>
        </div>

        {/* Add Product Form */}
        {showAddForm && (
          <div className="glass-card p-6 mb-6" data-testid="add-product-form">
            <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>New Product</h2>
            <form onSubmit={addProduct} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[#A1A7B5] text-xs mb-1 block">Product Name *</label>
                <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="e.g. BPC-157 5mg" required className="input-field w-full" data-testid="add-name" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[#A1A7B5] text-xs mb-1 block">Product Image</label>
                <div className="flex items-center gap-4">
                  {/* Preview */}
                  <div className="w-20 h-20 rounded-lg bg-[#111621] border border-[#1E2532] flex items-center justify-center overflow-hidden shrink-0">
                    {addForm.image_url ? (
                      <img src={addForm.image_url} alt="preview" className="w-full h-full object-cover" data-testid="add-image-preview" />
                    ) : (
                      <ImageIcon className="w-7 h-7 text-[#A1A7B5] opacity-40" />
                    )}
                  </div>
                  {/* Upload button + hidden file input */}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      onChange={(e) => uploadImage(e.target.files?.[0])}
                      className="hidden"
                      data-testid="add-image-file-input"
                    />
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/40"
                      data-testid="add-image-upload-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingImage ? 'Uploading…' : addForm.image_url ? 'Replace image' : 'Upload image'}
                    </Button>
                    {addForm.image_url && (
                      <button
                        type="button"
                        onClick={() => setAddForm({ ...addForm, image_url: '' })}
                        className="ml-3 text-xs text-[#A1A7B5] hover:text-red-400 transition-colors"
                        data-testid="add-image-remove"
                      >
                        Remove
                      </button>
                    )}
                    <p className="text-[#A1A7B5] text-xs mt-2">JPG, PNG, WebP, or GIF. Max 5 MB.</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[#A1A7B5] text-xs mb-1 block">Price ($) *</label>
                <input type="number" step="0.01" value={addForm.price} onChange={(e) => setAddForm({ ...addForm, price: e.target.value })} placeholder="49.99" required className="input-field w-full" data-testid="add-price" />
              </div>
              <div>
                <label className="text-[#A1A7B5] text-xs mb-1 block">Cost ($)</label>
                <input type="number" step="0.01" value={addForm.cost} onChange={(e) => setAddForm({ ...addForm, cost: e.target.value })} placeholder="15.00" className="input-field w-full" data-testid="add-cost" />
              </div>
              <div>
                <label className="text-[#A1A7B5] text-xs mb-1 block">Stock</label>
                <input type="number" value={addForm.stock} onChange={(e) => setAddForm({ ...addForm, stock: e.target.value })} className="input-field w-full" data-testid="add-stock" />
              </div>
              <div>
                <label className="text-[#A1A7B5] text-xs mb-1 block">Purity</label>
                <input value={addForm.purity} onChange={(e) => setAddForm({ ...addForm, purity: e.target.value })} placeholder="99%+" className="input-field w-full" data-testid="add-purity" />
              </div>
              <div>
                <label className="text-[#A1A7B5] text-xs mb-1 block">Molecular Weight</label>
                <input value={addForm.molecular_weight} onChange={(e) => setAddForm({ ...addForm, molecular_weight: e.target.value })} placeholder="1419.53 g/mol" className="input-field w-full" data-testid="add-mw" />
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={saving} className="btn-primary w-full py-2.5" data-testid="add-submit">
                  {saving ? 'Adding...' : 'Add Product'}
                </Button>
              </div>
              <div className="md:col-span-2">
                <label className="text-[#A1A7B5] text-xs mb-1 block">Description *</label>
                <textarea value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} placeholder="Detailed product description..." required rows={3} className="input-field w-full resize-none" data-testid="add-description" />
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="input-field w-full pl-10"
            data-testid="admin-products-search"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
        </div>

        {/* Products Table */}
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1E2532]">
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Product</th>
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Price</th>
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Cost</th>
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Margin</th>
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Stock</th>
                  <th className="text-left text-[#A1A7B5] text-sm font-medium p-4">Purity</th>
                  <th className="text-right text-[#A1A7B5] text-sm font-medium p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((product) => {
                  const margin = product.cost > 0 ? ((product.price - product.cost) / product.price * 100).toFixed(0) : '-';
                  const profit = product.cost > 0 ? (product.price - product.cost).toFixed(2) : '-';
                  return (
                  <tr key={product.id} className="border-b border-[#1E2532]/50 hover:bg-[#111621]/50" data-testid={`admin-product-row-${product.id}`}>
                    {editingId === product.id ? (
                      <>
                        <td className="p-4">
                          <input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="input-field w-full text-sm"
                            data-testid="edit-product-name"
                          />
                        </td>
                        <td className="p-4">
                          <div className="relative">
                            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A7B5]" />
                            <input
                              type="number"
                              step="0.01"
                              value={editForm.price}
                              onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                              className="input-field w-24 pl-7 text-sm"
                              data-testid="edit-product-price"
                            />
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="relative">
                            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A7B5]" />
                            <input
                              type="number"
                              step="0.01"
                              value={editForm.cost}
                              onChange={(e) => setEditForm({ ...editForm, cost: e.target.value })}
                              className="input-field w-24 pl-7 text-sm"
                              placeholder="0.00"
                              data-testid="edit-product-cost"
                            />
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-[#A1A7B5] text-sm">auto</span>
                        </td>
                        <td className="p-4">
                          <input
                            type="number"
                            value={editForm.stock}
                            onChange={(e) => setEditForm({ ...editForm, stock: e.target.value })}
                            className="input-field w-20 text-sm"
                            data-testid="edit-product-stock"
                          />
                        </td>
                        <td className="p-4">
                          <input
                            value={editForm.purity}
                            onChange={(e) => setEditForm({ ...editForm, purity: e.target.value })}
                            className="input-field w-24 text-sm"
                            data-testid="edit-product-purity"
                          />
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              onClick={() => saveProduct(product.id)}
                              disabled={saving}
                              className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1"
                              data-testid="save-product-btn"
                            >
                              <Save className="w-4 h-4 mr-1" />
                              {saving ? '...' : 'Save'}
                            </Button>
                            <Button
                              onClick={cancelEditing}
                              className="bg-[#1E2532] hover:bg-[#2a3040] text-white text-xs px-3 py-1"
                              data-testid="cancel-edit-btn"
                            >
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="p-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={product.image_url || '/placeholder.png'}
                              alt={product.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                            <div>
                              <p className="text-white font-medium text-sm">{product.name}</p>
                              <p className="text-[#A1A7B5] text-xs truncate max-w-[160px]">{product.description?.slice(0, 40)}...</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-white font-semibold">${product.price.toFixed(2)}</span>
                        </td>
                        <td className="p-4">
                          <span className={product.cost > 0 ? 'text-[#A1A7B5]' : 'text-[#A1A7B5]/50'}>
                            {product.cost > 0 ? `$${product.cost.toFixed(2)}` : 'Not set'}
                          </span>
                        </td>
                        <td className="p-4">
                          {margin !== '-' ? (
                            <div>
                              <span className="text-green-400 font-semibold text-sm">{margin}%</span>
                              <p className="text-[#A1A7B5] text-xs">${profit}</p>
                            </div>
                          ) : (
                            <span className="text-[#A1A7B5]/50 text-sm">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`font-medium ${product.stock >= 10 ? 'text-green-400' : product.stock > 3 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {product.stock}
                            {product.stock < 10 && <span className="text-xs ml-1">(low)</span>}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-[#A1A7B5] text-sm">{product.purity || '-'}</span>
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            onClick={() => startEditing(product)}
                            className="bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 text-[#00F0FF] text-xs px-3 py-1"
                            data-testid={`edit-product-${product.id}`}
                          >
                            Edit
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProductsPage;
